import * as v from 'valibot'
import {
  T2I2V_DEFAULT_MODEL,
  T2I2V_MODEL_OPTIONS,
} from 'src/services/helpers/t2i2v'

export const QualityTagSchema = v.object({
  prefix: v.optional(v.pipe(v.string(), v.maxLength(256))),
  suffix: v.optional(v.pipe(v.string(), v.maxLength(256))),
})

export const TaskParametersWorkflowSchema = v.looseObject({
  priority: v.number(),

  workflowId: v.string(),
  workflowName: v.string(),
  workflowVersionId: v.string(),
  inputs: v.record(v.string(), v.unknown()),
})

export const AudioLanguagePicklist = v.picklist([
  'none',
  'chinese',
  'english',
  'japanese',
  'korean',
])
export type AudioLanguage = v.InferOutput<typeof AudioLanguagePicklist>

/**
 * Canonical entries for the `i2vPro` inner object. Extracted as a named const
 * so that downstream consumers (e.g. the I2V task-import parser) can derive a
 * matching shape from the same source of truth — adding a new field here
 * propagates to both the form type and the import parser via reflection over
 * `Object.keys(...)`.
 */
export const I2vProInnerEntries = {
  mode: v.optional(v.picklist(['basic', 'professional'])),
  // Only check if model is a string, not a specific model ID
  // The already offline models cannot be selected anyway
  model: v.string(),
  mediaId: v.string(),
  tailMediaId: v.optional(v.string()),
  prompts: v.optional(v.string()),
  duration: v.optional(v.picklist(['5', '10', '15'])),
  negativePrompts: v.optional(v.string()),
  cameraMovement: v.optional(v.string()),
  usePromptsHelper: v.optional(v.boolean()),
  controlMediaId: v.optional(v.string()),
  controlMediaType: v.optional(v.string()),
  generateAudio: v.optional(v.boolean()),
  audioLanguage: v.optional(AudioLanguagePicklist),
} satisfies Record<string, v.GenericSchema<unknown, unknown>>

/** Canonical entries for the `referenceVideo` inner object. See {@link I2vProInnerEntries}. */
export const ReferenceVideoInnerEntries = {
  mode: v.optional(v.picklist(['basic', 'professional'])),
  model: v.string(),
  prompt: v.optional(v.string()),
  duration: v.optional(v.union([v.number(), v.string()])),
  referenceImageMediaIds: v.optional(v.array(v.string())),
  referenceVideoMediaIds: v.optional(v.array(v.string())),
  referenceAudioMediaIds: v.optional(v.array(v.string())),
  generateAudio: v.optional(v.boolean()),
  audioLanguage: v.optional(AudioLanguagePicklist),
} satisfies Record<string, v.GenericSchema<unknown, unknown>>

export const TaskParametersI2vProSchema = v.looseObject({
  width: v.number(),
  height: v.number(),
  /** This modelId is a hardcoded placeholder; Mobile apps need a valid model ID, will crash otherwise */
  modelId: v.optional(v.string()),
  /** Hardcoded to 1000 */
  priority: v.number(),
  i2vPro: v.object(I2vProInnerEntries),
  extra: v.optional(
    v.object({
      naturalPrompts: v.optional(v.string()),
      referenceImagePrompts: v.optional(v.string()),
    }),
  ),
  sceneId: v.optional(v.string()),
})

export const TaskParametersReferenceVideoSchema = v.looseObject({
  width: v.optional(v.number()),
  height: v.optional(v.number()),
  modelId: v.optional(v.string()),
  priority: v.optional(v.number()),
  referenceVideo: v.object(ReferenceVideoInnerEntries),
  sceneId: v.optional(v.string()),
  extra: v.optional(
    v.object({
      naturalPrompts: v.optional(v.string()),
      referenceImagePrompts: v.optional(v.string()),
    }),
  ),
})

export const TaskParametersGeneratorSchema = v.looseObject({
  /** Few tasks don't have a valid priority (which should not happen) */
  priority: v.optional(v.number()),

  seed: v.optional(v.union([v.string(), v.number()])),
  width: v.number(),
  height: v.number(),
  /** Usually treated as "Base Media ID" */
  mediaId: v.optional(v.string()),
  /** Some ancient tasks don't have a modelId */
  modelId: v.optional(v.string()),
  /** Task Parameters from Artwork has omitted prompts */
  prompts: v.optional(v.string()),
  /** AnimateDiff tasks don't have cfgScale */
  cfgScale: v.optional(v.number()),
  /** AnimateDiff tasks don't have samplingSteps */
  samplingSteps: v.optional(v.number()),
  /** AnimateDiff tasks don't have samplingMethod */
  samplingMethod: v.optional(v.string()),
  /** Some tasks don't have negativePrompts */
  negativePrompts: v.optional(v.string()),

  /** Optional scene id used by certain presets/growth events */
  sceneId: v.optional(v.string()),

  clipSkip: v.optional(v.number()),
  batchSize: v.optional(v.number()),

  controlNets: v.optional(
    v.array(
      v.looseObject({
        type: v.string(),
        mediaId: v.string(),
        weight: v.optional(v.number()),
        thresholdA: v.optional(v.number()),
        maskMediaId: v.optional(v.string()),
      }),
    ),
  ),

  qualityTag: v.optional(QualityTagSchema),
  lightning: v.optional(v.boolean()),
  watermark: v.optional(v.picklist(['pixai'])),

  // hires/upscale, conflicts with enlarge
  upscale: v.optional(v.number()),
  upscaleDenoisingSteps: v.optional(v.number()),
  upscaleDenoisingStrength: v.optional(v.number()),

  // enlarge, conflicts with upscale
  enlarge: v.optional(v.number()),
  enlargeModel: v.optional(v.string()),

  // FaceFix, conflicts with enlarge
  enableADetailer: v.optional(v.boolean()),

  styleMix: v.optional(
    v.object({
      enabled: v.optional(v.boolean()),
      seed: v.optional(v.number()),
    }),
  ),

  inferenceProfile: v.optional(v.string()),

  modelStyle: v.optional(
    v.union([
      v.object({ type: v.literal('key'), keyId: v.string() }),
      v.object({ type: v.literal('custom'), custom: v.string() }),
      v.object({ type: v.literal('styleKeyRef'), refId: v.string() }),
    ]),
  ),

  /**
   * Only available for specific models, e.g. hinata
   */
  inferenceConfig: v.optional(
    v.object({
      /** Explicitly set to null to disable this feature on supported models */
      rescaleCFG: v.optional(v.union([v.number(), v.null()])),
      modelSampling: v.optional(
        v.object({
          inputs: v.optional(
            v.object({
              shift: v.optional(v.number()),
            }),
          ),
        }),
      ),
    }),
  ),

  promptHelper: v.optional(
    v.object({
      /**
       * @deprecated
       */
      withStage: v.optional(v.boolean()),

      // stands for whether user switch on the switcher on panel
      userWantToEnable: v.optional(v.boolean()),

      // stands for whether enable prompt helper
      enable: v.optional(v.boolean()),
    }),
  ),
})

export const TaskParametersT2i2vSchema = v.looseObject({
  ...TaskParametersGeneratorSchema.entries,

  t2i2v: v.looseObject({
    duration: v.optional(v.string(), '5'),
    mode: v.optional(v.picklist(['basic', 'professional'])),
    // Only allow in-house i2v models: framepack (v2.5.1), wan22 (v3.0), and ltx23 (v3.0.2)
    model: v.optional(v.picklist(T2I2V_MODEL_OPTIONS), T2I2V_DEFAULT_MODEL),
  }),
})

export const TaskParametersArtFilterSchema = v.looseObject({
  model: v.literal('pixai-image-filter'),
  mediaId: v.string(),
  inputs: v.object({
    filterId: v.string(),
    strength: v.optional(v.number()),
  }),
})
